import React, { useState, useEffect, useMemo } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  VStack,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  AlertDialogCloseButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  UnorderedList,
  ListItem,
  HStack,
  Checkbox,
  IconButton,
  Tooltip,
  Input,
  InputGroup,
  InputLeftElement,
  ButtonGroup,
  Icon,
  Select,
  Badge,
} from '@chakra-ui/react';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  EditIcon,
  SearchIcon,
  WarningIcon,
} from '@chakra-ui/icons';
import {
  FaSort,
  FaSortUp,
  FaSortDown,
  FaPhoneSlash,
  FaEnvelopeOpen,
  FaExclamationTriangle,
} from 'react-icons/fa';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender
} from '@tanstack/react-table';
import TransferModal from './TransferModal';
import unifiedEmailService from '../../services/unifiedEmailService';
import { validateEmailAddress } from '../../utils/emailAddressValidation';
import { validatePhoneNumber, formatPhoneNumber } from '../../utils/phoneNumberValidation';

// Temporarily comment out debugging and import
// console.log('Current module location:', import.meta.url);
// console.log('Attempting absolute import from:', '@/services/unifiedEmailService');
// console.log('Attempting relative import from:', '../../services/unifiedEmailService');

// Try both ways
// import unifiedEmailService from '@/services/unifiedEmailService';
// import unifiedEmailService from '../../services/unifiedEmailService';

// console.log('UnifiedEmailService loaded:', unifiedEmailService);

const cleanPhoneNumber = (phone) => {
  if (!phone) return '';
  
  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Handle different cases
  if (cleaned.length === 10) {
    return cleaned;
  } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return cleaned.substring(1); // Remove the leading 1
  }
  
  return ''; // Return empty if invalid
};

// Helper to convert to E.164 format
const toE164 = (phone) => {
  if (!phone) return null;
  return phone.length === 10 ? `+1${phone}` : null;
};

const EditableCell = ({ getValue, row, column, table }) => {
  const initialValue = getValue();
  const [value, setValue] = useState(initialValue);
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const toast = useToast();

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const onBlur = async () => {
    try {
      if (isUpdating) return;
      setIsUpdating(true);
      setIsEditing(false);
      
      const newValue = value?.trim() || '';

      // Check if this would make all fields empty
      const otherFieldsEmpty = ['first_name', 'last_name', 'email', 'phone_number']
        .filter(field => field !== column.id)
        .every(field => !row.original[field] || row.original[field].trim() === '');

      if (otherFieldsEmpty && newValue === '') {
        setValue(initialValue); // Revert the change
        toast({
          title: "Cannot clear all fields",
          description: "At least one field must contain data",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Validate email format if this is the email field
      if (column.id === 'email') {
        const isInvalid = newValue && !validateEmailAddress(newValue, false);
        const updates = {
          email: newValue,
          has_invalid_email: isInvalid
        };
        
        if (isInvalid) {
          setValue(initialValue); // Revert the change
          toast({
            title: "Invalid Email Address",
            description: "Please enter a valid email address",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          return;
        }
        
        await table.options.meta?.updateData(row.index, updates);
        return;
      }

      // Validate phone number if this is the phone field
      if (column.id === 'phone_number') {
        const formattedPhone = formatPhoneNumber(newValue);
        const isInvalid = newValue && !validatePhoneNumber(newValue, false);
        const updates = {
          phone_number: newValue,
          cleaned_phone_number: formattedPhone,
          has_invalid_phone: isInvalid
        };

        if (isInvalid) {
          setValue(initialValue); // Revert the change
          toast({
            title: "Invalid Phone Number",
            description: "Please enter a valid 10-digit phone number. You may include a leading '1' or formatting characters.",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          return;
        }

        await table.options.meta?.updateData(row.index, updates);
        return;
      }

      // For other fields, just update normally
      await table.options.meta?.updateData(row.index, column.id, newValue);
    } catch (error) {
      console.error('Error in onBlur:', error);
      toast({
        title: "Error updating field",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setValue(initialValue); // Revert on error
    } finally {
      setIsUpdating(false);
    }
  };

  if (!isEditing) {
    return (
      <Box
        onClick={() => setIsEditing(true)}
        py={1}
        px={2}
        _hover={{ bg: "gray.50" }}
        cursor="pointer"
        minH="32px"
        display="flex"
        alignItems="center"
      >
        {value || ''}
      </Box>
    );
  }

  return (
    <Box
      bg="blue.50"
      borderRadius="sm"
      p={0.5}
    >
      <Input
        value={value}
        onChange={e => setValue(e.target.value)}
        onBlur={onBlur}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            onBlur();
          } else if (e.key === 'Escape') {
            setIsEditing(false);
            setValue(initialValue);
          }
        }}
        size="sm"
        variant="unstyled"
        px={2}
        autoFocus
        bg="white"
        borderRadius="sm"
        isDisabled={isUpdating}
      />
    </Box>
  );
};

const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value, onChange, debounce]);

  return (
    <InputGroup size="xs">
      <InputLeftElement pointerEvents="none">
        <SearchIcon color="gray.400" />
      </InputLeftElement>
      <Input
        {...props}
        value={value}
        onChange={e => setValue(e.target.value)}
      />
    </InputGroup>
  );
};

const ValidationCell = ({ value }) => (
  <Flex justify="center">
    {value ? (
      <Text color="red.500" fontSize="sm">❌</Text>
    ) : (
      <Text color="green.500" fontSize="sm">✓</Text>
    )}
  </Flex>
);

const MissingFieldsCell = ({ row }) => {
  const missingFields = [];
  if (!row.original.first_name) missingFields.push('First Name');
  if (!row.original.last_name) missingFields.push('Last Name');
  if (!row.original.email) missingFields.push('Email');
  if (!row.original.phone_number) missingFields.push('Phone');
  
  return (
    <Box px={2} py={1}>
      <Text fontSize="xs" color="red.500">
        {missingFields.join(', ')}
      </Text>
    </Box>
  );
};

const ColumnHeader = ({ header, children }) => {
  const canSort = header.column.getCanSort();
  const sortDirection = header.column.getIsSorted();
  
  return (
    <Flex 
      alignItems="center" 
      justify="center"
      minH="24px"
      onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
      cursor={canSort ? 'pointer' : 'default'}
      _hover={canSort ? { color: 'blue.600' } : undefined}
      position="relative"
      gap={2}
    >
      <Box fontSize="sm" fontWeight="semibold" color="gray.900">{children}</Box>
      {canSort && (
        <Box 
          color={sortDirection ? 'blue.500' : 'gray.500'}
          transition="all 0.2s"
          _hover={{ color: 'blue.600' }}
          display="flex"
          alignItems="center"
        >
          {sortDirection ? (
            sortDirection === 'asc' ? (
              <FaSortUp size={16} />
            ) : (
              <FaSortDown size={16} />
            )
          ) : (
            <FaSort size={14} />
          )}
        </Box>
      )}
    </Flex>
  );
};

const TransferSummaryDialog = ({ isOpen, onClose, summary }) => {
  if (!summary) return null;
  
  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={React.useRef()}
      onClose={onClose}
      size="xl"
      scrollBehavior="inside"
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader>Transfer Summary</AlertDialogHeader>
          <AlertDialogCloseButton />
          <AlertDialogBody>
            <VStack align="stretch" spacing={6}>
              <Box>
                <Text fontWeight="bold" mb={2}>Transfer Mode:</Text>
                <Text>{summary.mode}</Text>
                {summary.mode === 'test' && (
                  <Text color="gray.600" fontSize="sm">
                    All emails will be sent to: {summary.testEmail}
                  </Text>
                )}
              </Box>

              <Box>
                <Text fontWeight="bold" mb={2}>Statistics:</Text>
                <UnorderedList>
                  <ListItem>Total Records Selected: {summary.totalSelected}</ListItem>
                  <ListItem>Successfully Transferred: {summary.transferred}</ListItem>
                  <ListItem>Skipped (Duplicates): {summary.duplicates}</ListItem>
                </UnorderedList>
              </Box>

              {summary.skippedContacts?.length > 0 && (
                <Box>
                  <Text fontWeight="bold" color="orange.500">Skipped Records:</Text>
                  <UnorderedList>
                    {summary.skippedContacts.map((contact, index) => (
                      <ListItem key={index}>
                        {contact.name} ({contact.phone}) - {contact.reason}
                      </ListItem>
                    ))}
                  </UnorderedList>
                </Box>
              )}

              {summary.contacts?.length > 0 && (
                <Box>
                  <Text fontWeight="bold">Transferred Contacts:</Text>
                  <UnorderedList>
                    {summary.contacts.map((contact, index) => (
                      <ListItem key={index}>
                        {contact.name} ({contact.phone})
                      </ListItem>
                    ))}
                  </UnorderedList>
                </Box>
              )}

              <Box>
                <Text fontWeight="bold" size="lg" mb={4}>Database Changes:</Text>
                <VStack align="stretch" spacing={4}>
                  <Box>
                    <Text fontWeight="bold" mb={2}>Records Added to texting_contacts:</Text>
                    <UnorderedList>
                      {summary.databaseChanges.texting_contacts.map((contact, index) => (
                        <ListItem key={index}>
                          {contact.name} (ID: {contact.id})
                          <UnorderedList>
                            <ListItem>Phone: {contact.phone}</ListItem>
                            <ListItem>Email: {contact.email}</ListItem>
                            <ListItem>Status: {contact.consent_status}</ListItem>
                            <ListItem>Created: {new Date(contact.created_at).toLocaleString()}</ListItem>
                          </UnorderedList>
                        </ListItem>
                      ))}
                    </UnorderedList>
                  </Box>

                  <Box>
                    <Text fontWeight="bold" mb={2}>Records Added to contact_audit_log:</Text>
                    <UnorderedList>
                      {summary.databaseChanges.contact_audit_log.map((log, index) => (
                        <ListItem key={index}>
                          Contact ID: {log.contact_id}
                          <UnorderedList>
                            <ListItem>Action: {log.action}</ListItem>
                            <ListItem>Changed By: {log.changed_by}</ListItem>
                          </UnorderedList>
                        </ListItem>
                      ))}
                    </UnorderedList>
                  </Box>

                  <Box>
                    <Text fontWeight="bold" mb={2}>Records Added to consent_audit_log:</Text>
                    <UnorderedList>
                      {summary.databaseChanges.consent_audit_log.map((log, index) => (
                        <ListItem key={index}>
                          Contact ID: {log.contact_id}
                          <UnorderedList>
                            <ListItem>Action: {log.action}</ListItem>
                            <ListItem>Previous Status: {log.previous_status}</ListItem>
                            <ListItem>New Status: {log.new_status}</ListItem>
                            <ListItem>Changed By Type: {log.changed_by_type}</ListItem>
                            <ListItem>Phone: {log.phone_number}</ListItem>
                            {log.metadata && (
                              <ListItem>
                                Metadata:
                                <UnorderedList>
                                  <ListItem>Source: {log.metadata.source}</ListItem>
                                  <ListItem>Transfer Mode: {log.metadata.transfer_mode}</ListItem>
                                  <ListItem>Imported By: {log.metadata.imported_by}</ListItem>
                                  <ListItem>Import Batch: {new Date(log.metadata.import_batch).toLocaleString()}</ListItem>
                                </UnorderedList>
                              </ListItem>
                            )}
                          </UnorderedList>
                        </ListItem>
                      ))}
                    </UnorderedList>
                  </Box>

                  <Box>
                    <Text fontWeight="bold" mb={2}>Records Added to email_logs:</Text>
                    <UnorderedList>
                      {summary.databaseChanges.email_logs?.map((log, index) => (
                        <ListItem key={index}>
                          Message ID: {log.message_id}
                          <UnorderedList>
                            <ListItem>Recipient: {log.recipient_email}</ListItem>
                            <ListItem>Template: {log.template_name}</ListItem>
                            <ListItem>Subject: {log.subject}</ListItem>
                          </UnorderedList>
                        </ListItem>
                      ))}
                    </UnorderedList>
                  </Box>
                </VStack>
              </Box>

              <Box>
                <Text fontWeight="bold">Next Steps:</Text>
                <UnorderedList>
                  <ListItem>Welcome emails have been queued for all transferred contacts</ListItem>
                  {summary.mode === 'test' ? (
                    <ListItem>Since this was a test, all emails will be sent to your email address</ListItem>
                  ) : (
                    <ListItem>Emails will be sent to each contact's email address</ListItem>
                  )}
                </UnorderedList>
              </Box>
            </VStack>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={React.useRef()} onClick={onClose}>
              Close
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

const TransferMemberImport = () => {
  const supabase = useSupabaseClient();
  const toast = useToast();
  const [data, setData] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [isRemoving, setIsRemoving] = useState(false);
  const [transferMode, setTransferMode] = useState('test');
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [testEmail, setTestEmail] = useState(''); // Will be set from user profile
  const [showSummary, setShowSummary] = useState(false);
  const [transferSummary, setTransferSummary] = useState(null);
  const summaryRef = React.useRef();

  const VALID_CONSENT_STATUSES = ['PENDING', 'CONSENTED', 'REVOKED'];
  const VALID_AUDIT_ACTIONS = ['CREATE', 'UPDATE', 'DELETE', 'INSERT'];
  const VALID_TRANSFER_MODES = ['test', 'production', 'simulation'];

  // Get selected row IDs
  const selectedRows = useMemo(() => {
    return Object.keys(rowSelection)
      .filter(key => rowSelection[key])
      .map(index => data[parseInt(index)]?.id)
      .filter(Boolean);
  }, [data, rowSelection]);

  // Calculate validation summary for selected records
  const validationSummary = useMemo(() => {
    const selected = data.filter(row => selectedRows.includes(row.id));
    const summary = {
      totalSelected: selected.length,
      complete: 0,
      incomplete: 0,
      missingFirstName: 0,
      missingLastName: 0,
      missingEmail: 0,
      invalidEmail: 0,
      missingPhone: 0,
      invalidPhone: 0,
    };

    selected.forEach(row => {
      const hasFirstName = row.first_name?.trim();
      const hasLastName = row.last_name?.trim();
      const hasEmail = row.email?.trim();
      const hasValidEmail = hasEmail && validateEmailAddress(hasEmail, false); // Don't allow empty emails
      const hasPhone = row.phone_number?.trim();
      const hasValidPhone = row.cleaned_phone_number?.length === 10;

      if (!hasFirstName) summary.missingFirstName++;
      if (!hasLastName) summary.missingLastName++;
      if (!hasEmail) summary.missingEmail++;
      if (hasEmail && !hasValidEmail) summary.invalidEmail++;
      if (!hasPhone) summary.missingPhone++;
      if (hasPhone && !hasValidPhone) summary.invalidPhone++;

      if (
        hasFirstName &&
        hasLastName &&
        hasEmail &&
        hasValidEmail &&
        hasPhone &&
        hasValidPhone
      ) {
        summary.complete++;
      } else {
        summary.incomplete++;
      }
    });

    return summary;
  }, [data, selectedRows]);

  // Get user profile for test email
  useEffect(() => {
    const getProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.email) {
        setTestEmail(user.email);
      }
    };
    getProfile();
  }, [supabase]);

  const handleTransferModeChange = (event) => {
    setTransferMode(event.target.value);
  };

  const handleUnselectIncomplete = () => {
    const newSelection = {};
    data.forEach((row, index) => {
      const hasFirstName = row.first_name?.trim();
      const hasLastName = row.last_name?.trim();
      const hasEmail = row.email?.trim();
      const hasValidEmail = hasEmail && validateEmailAddress(hasEmail, false); // Don't allow empty emails
      const hasPhone = row.phone_number?.trim();
      const hasValidPhone = row.cleaned_phone_number?.length === 10;

      if (
        hasFirstName &&
        hasLastName &&
        hasEmail &&
        hasValidEmail &&
        hasPhone &&
        hasValidPhone &&
        rowSelection[index]
      ) {
        newSelection[index] = true;
      }
    });
    setRowSelection(newSelection);
  };

  const handleTransfer = async () => {
    try {
      // Validate transfer mode
      if (!VALID_TRANSFER_MODES.includes(transferMode)) {
        throw new Error(`Invalid transfer mode. Must be one of: ${VALID_TRANSFER_MODES.join(', ')}`);
      }

      // Get user for audit and test email
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      console.log('Selected row IDs:', selectedRows);
      console.log('All data:', data);
      
      const recordsToTransfer = data.filter(row => {
        const hasFirstName = row.first_name?.trim();
        const hasLastName = row.last_name?.trim();
        const hasEmail = row.email?.trim();
        const hasValidEmail = hasEmail && validateEmailAddress(hasEmail, false); // Don't allow empty emails
        const hasPhone = row.phone_number?.trim();
        const hasValidPhone = row.cleaned_phone_number?.length === 10;

        const isSelected = selectedRows.includes(row.id);
        const isComplete = hasFirstName &&
          hasLastName &&
          hasEmail &&
          hasValidEmail &&
          hasPhone &&
          hasValidPhone;

        if (isSelected && !isComplete) {
          console.log('Selected but incomplete record:', {
            id: row.id,
            hasFirstName,
            hasLastName,
            hasEmail,
            hasValidEmail,
            hasPhone,
            hasValidPhone
          });
        }

        return isSelected && isComplete;
      });

      console.log('Records to transfer:', recordsToTransfer);

      if (recordsToTransfer.length === 0) {
        toast({
          title: "No records to transfer",
          description: "No complete records were selected for transfer.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        setShowTransferModal(false);
        return;
      }

      // Check for duplicate phone numbers
      const { data: existingContacts, error: checkError } = await supabase
        .from('texting_contacts')
        .select('phone_number')
        .in('phone_number', recordsToTransfer.map(r => toE164(r.cleaned_phone_number)));

      if (checkError) throw checkError;

      console.log('Existing contacts:', existingContacts);
      
      const existingPhoneSet = new Set(
        existingContacts?.map(c => c.phone_number.replace(/^\+1/, '')) || []
      );

      // Separate records into new and duplicate
      const duplicateRecords = recordsToTransfer.filter(r => existingPhoneSet.has(r.cleaned_phone_number));
      const newRecords = recordsToTransfer.filter(r => !existingPhoneSet.has(r.cleaned_phone_number));

      if (newRecords.length === 0) {
        toast({
          title: "No new records to transfer",
          description: "All selected records already exist in the contacts list.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        setShowTransferModal(false);
        return;
      }

      // 1. Insert into texting_contacts
      const textingContacts = newRecords.map(record => ({
        first_name: record.first_name.trim(),
        last_name: record.last_name.trim(),
        email: record.email?.trim() || null,
        phone_number: toE164(record.cleaned_phone_number),
        consent_status: 'PENDING',
        is_rotary_member: true,
        created_at: new Date().toISOString()
        // Removed full_name (handled by trigger)
        // Removed updated_at (handled by trigger)
      }));

      const { data: insertedContacts, error: insertError } = await supabase
        .from('texting_contacts')
        .insert(textingContacts)
        .select();

      if (insertError) throw insertError;

      // 2. Insert consent audit logs for the initial PENDING status
      const consentAuditLogs = insertedContacts.map(contact => ({
        contact_id: contact.id,
        action: 'REQUEST_SENT',
        previous_status: null,
        new_status: 'PENDING',
        changed_by: null,
        changed_by_type: 'SYSTEM',
        phone_number: contact.phone_number,
        metadata: {
          source: 'member_import',
          transfer_mode: transferMode,
          imported_by: user.id,
          import_batch: new Date().toISOString()
        }
      }));

      const { error: consentAuditError } = await supabase
        .from('consent_audit_log')
        .insert(consentAuditLogs);

      if (consentAuditError) throw consentAuditError;

      // 3. Insert contact audit logs
      const auditLogs = insertedContacts.map(contact => ({
        action: 'INSERT',
        contact_id: contact.id,
        changed_by: user.id,
        new_data: contact,
        changes: {
          first_name: contact.first_name,
          last_name: contact.last_name,
          email: contact.email,
          phone_number: contact.phone_number,
          consent_status: contact.consent_status,
          is_rotary_member: contact.is_rotary_member
          // Removed full_name (handled by trigger)
        }
      }));

      const { error: auditError } = await supabase
        .from('contact_audit_log')
        .insert(auditLogs);

      if (auditError) throw auditError;

      // 4. Create email logs and send welcome emails
      const emailResults = await Promise.allSettled(insertedContacts.map(async contact => {
        try {
          // First fetch the welcome_email template
          const { data: template, error: templateError } = await supabase
            .from('email_templates')
            .select('*')
            .eq('name', 'welcome_email')
            .single();

          if (templateError) throw templateError;
          if (!template) throw new Error('Welcome email template not found');

          // Update consent audit log with email template info
          const { error: updateAuditError } = await supabase
            .from('consent_audit_log')
            .update({ 
              email_template_id: template.id,
              email_template_name: template.name
            })
            .eq('contact_id', contact.id)
            .eq('action', 'REQUEST_SENT');

          if (updateAuditError) {
            console.error('Failed to update consent audit log with template info:', updateAuditError);
          }

          await unifiedEmailService.sendEmail({
            to: transferMode === 'test' ? user.email : contact.email,
            subject: template.subject,
            html: template.html_content,
            text: template.text_content,
            variables: {
              firstName: contact.first_name,
              lastName: contact.last_name,
              fullName: `${contact.first_name} ${contact.last_name}`,
              email: contact.email,
              phone: contact.phone_number
            },
            template_id: template.id,
            supabase
          });
          return { success: true, contact };
        } catch (error) {
          console.error('Failed to send welcome email:', error);
          return { success: false, error, contact };
        }
      }));

      // Check for any failed emails
      const failedEmails = emailResults.filter(result => 
        result.status === 'rejected' || (result.status === 'fulfilled' && !result.value.success)
      );

      if (failedEmails.length > 0) {
        console.warn('Some welcome emails failed to send:', failedEmails);
        toast({
          title: 'Some welcome emails failed to send',
          description: `${failedEmails.length} out of ${emailResults.length} emails failed to send. The contacts were still transferred successfully.`,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }

      // 5. Update import_contacts
      const { error: updateError } = await supabase
        .from('import_contacts')
        .update({ 
          is_processed: true,
          transfer_mode: transferMode
        })
        .in('id', newRecords.map(r => r.id));

      if (updateError) throw updateError;

      // 6. Refresh the data
      const { data: updatedContacts, error: fetchError } = await supabase
        .from('import_contacts')
        .select('*')
        .eq('is_removed', false)
        .eq('is_processed', false)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      
      setData(updatedContacts || []);
      setRowSelection({});

      // After successful transfer, prepare summary
      const summary = {
        totalSelected: recordsToTransfer.length,
        duplicates: recordsToTransfer.length - newRecords.length,
        transferred: newRecords.length,
        mode: transferMode,
        testEmail: transferMode === 'test' ? user.email : null,
        skippedContacts: duplicateRecords.map(contact => ({
          name: `${contact.first_name} ${contact.last_name}`,
          phone: contact.phone_number,
          email: contact.email,
          reason: 'Already exists in the member list'
        })),
        contacts: insertedContacts.map(contact => ({
          name: `${contact.first_name} ${contact.last_name}`,
          phone: contact.phone_number,
          email: contact.email
        })),
        databaseChanges: {
          texting_contacts: insertedContacts.map(contact => ({
            id: contact.id,
            name: `${contact.first_name} ${contact.last_name}`,
            phone: contact.phone_number,
            email: contact.email,
            consent_status: contact.consent_status,
            created_at: contact.created_at
          })),
          contact_audit_log: auditLogs.map(log => ({
            id: log.id,
            action: log.action,
            contact_id: log.contact_id,
            changed_by: log.changed_by,
            changes: log.changes
          })),
          consent_audit_log: consentAuditLogs.map(log => ({
            contact_id: log.contact_id,
            action: log.action,
            previous_status: log.previous_status,
            new_status: log.new_status,
            changed_by_type: log.changed_by_type,
            phone_number: log.phone_number,
            metadata: log.metadata
          })),
          email_logs: emailResults.filter(result => result.status === 'fulfilled' && result.value.success).map(result => ({
            message_id: result.value.contact.id,
            recipient_email: result.value.contact.email,
            template_name: 'welcome_email',
            subject: 'Welcome to Rotary',
          })),
        }
      };
      
      setTransferSummary(summary);
      setShowSummary(true);
      setShowTransferModal(false);

    } catch (error) {
      console.error('Error in transfer:', error);
      toast({
        title: "Transfer Failed",
        description: error.message || 'An unexpected error occurred',
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setShowTransferModal(false);
    }
  };

  // Fetch import_contacts data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsRemoving(true);
        const { data: contacts, error } = await supabase
          .from('import_contacts')
          .select('*')
          .eq('is_removed', false)  // Only fetch non-removed records
          .eq('is_processed', false)  // Only fetch unprocessed records
          .order('created_at', { ascending: false });

        if (error) throw error;
        
        console.log('Fetched contacts:', contacts?.length || 0);
        setData(contacts || []);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast({
          title: 'Error fetching data',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsRemoving(false);
      }
    };

    fetchData();
  }, []); // Only fetch on mount

  const updateData = async (rowIndex, columnId, value, additionalUpdates = {}) => {
    try {
      const row = data[rowIndex];
      if (!row?.id) {
        console.error('Row ID not found');
        return;
      }

      // Prepare the update object
      const updates = {
        [columnId]: value,
        ...additionalUpdates
      };

      // Track changed fields
      const changedFields = {};
      Object.keys(updates).forEach(field => {
        if (row[field] !== updates[field]) {
          changedFields[field] = {
            old: row[field],
            new: updates[field]
          };
        }
      });

      if (Object.keys(changedFields).length === 0) {
        console.log('No changes detected');
        return;
      }

      const updatePayload = {
        ...updates,
        changed_fields: changedFields
      };

      console.log('Attempting database update with:', updatePayload);

      const { error } = await supabase
        .from('import_contacts')
        .update(updatePayload)
        .eq('id', row.id);

      if (error) throw error;

      // Update local state
      setData(old =>
        old.map((row, index) =>
          index === rowIndex
            ? { ...row, ...updates }
            : row
        )
      );

      // If this is a phone number, update the cleaned phone and validation
      if (columnId === 'phone_number') {
        const cleanedPhone = cleanPhoneNumber(value);
        await updateData(rowIndex, 'cleaned_phone_number', cleanedPhone, {
          has_invalid_phone: value ? !cleanedPhone : false
        });
      }
    } catch (error) {
      console.error('Error updating data:', error);
      throw error;
    }
  };

  const columns = useMemo(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Box textAlign="center">
            <Checkbox
              isChecked={table.getIsAllRowsSelected()}
              isIndeterminate={table.getIsSomeRowsSelected()}
              onChange={table.getToggleAllRowsSelectedHandler()}
            />
          </Box>
        ),
        cell: ({ row }) => (
          <Box textAlign="center">
            <Checkbox
              isChecked={row.getIsSelected()}
              isIndeterminate={row.getIsSomeSelected()}
              onChange={row.getToggleSelectedHandler()}
            />
          </Box>
        ),
        size: 30,
      },
      {
        accessorKey: 'first_name',
        header: 'First Name',
        cell: EditableCell,
        size: 150,
      },
      {
        accessorKey: 'last_name',
        header: 'Last Name',
        cell: EditableCell,
        size: 150,
      },
      {
        accessorKey: 'email',
        header: 'Email',
        cell: EditableCell,
        size: 250,
      },
      {
        accessorKey: 'phone_number',
        header: 'Phone',
        cell: EditableCell,
        size: 120,
      },
      {
        accessorKey: 'cleaned_phone_number',
        header: 'Cleaned Phone',
        cell: info => info.getValue(),
        size: 120,
      },
      {
        id: 'phoneConflict',
        header: () => (
          <Tooltip label="Phone Conflicts">
            <Box w="20px">
              <Icon as={FaExclamationTriangle} boxSize="14px" />
            </Box>
          </Tooltip>
        ),
        accessorFn: row => row.has_phone_conflict,
        cell: ({ getValue }) => (
          <Box display="flex" alignItems="center" justifyContent="center">
            {getValue() && <Icon as={FaExclamationTriangle} color="orange.500" />}
          </Box>
        ),
        size: 20,
      },
      {
        id: 'invalidPhone',
        header: () => (
          <Tooltip label="Invalid Phone">
            <Box w="20px">
              <Icon as={FaPhoneSlash} boxSize="14px" />
            </Box>
          </Tooltip>
        ),
        accessorFn: row => row.has_invalid_phone,
        cell: ({ getValue }) => (
          <Box display="flex" alignItems="center" justifyContent="center">
            {getValue() && <Icon as={FaPhoneSlash} color="red.500" />}
          </Box>
        ),
        size: 20,
      },
      {
        id: 'invalidEmail',
        header: () => (
          <Tooltip label="Invalid Email">
            <Box w="20px" display="flex" alignItems="center" justifyContent="center">
              <Icon as={FaEnvelopeOpen} boxSize="14px" />
            </Box>
          </Tooltip>
        ),
        accessorFn: row => row.has_invalid_email,
        cell: ({ getValue }) => (
          <Box display="flex" alignItems="center" justifyContent="center">
            {getValue() && <Icon as={FaEnvelopeOpen} color="red.500" />}
          </Box>
        ),
        size: 20,
      },
      {
        id: 'missing_fields',
        header: 'Missing Fields',
        cell: MissingFieldsCell,
        size: 150,
        accessorFn: row => {
          const fields = [];
          if (!row.first_name) fields.push('First Name');
          if (!row.last_name) fields.push('Last Name');
          if (!row.email) fields.push('Email');
          if (!row.phone_number) fields.push('Phone');
          return fields.join(', ');
        },
      },
    ],
    []
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      rowSelection,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    meta: {
      updateData,
    },
  });

  const handleRemoveSelected = async () => {
    if (selectedRows.length === 0) {
      toast({
        title: "No records selected",
        description: "Please select records to remove",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsRemoving(true);
    try {
      const selectedIds = selectedRows;
      const { error } = await supabase
        .from('import_contacts')
        .update({ is_removed: true })
        .in('id', selectedIds);

      if (error) throw error;

      // Update local state to remove these rows
      setData(prevData => prevData.filter(row => !selectedRows.includes(row.id)));
      setRowSelection({});

      toast({
        title: "Records removed",
        description: `${selectedIds.length} records have been marked for removal`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error removing records:', error);
      toast({
        title: "Error removing records",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsRemoving(false);
    }
  };

  const handleRemoveConflicts = async () => {
    setIsRemoving(true);
    try {
      const { error } = await supabase
        .from('import_contacts')
        .update({ is_removed: true })
        .eq('has_phone_conflict', true)
        .eq('is_removed', false);

      if (error) throw error;

      // Update local state to remove conflicting rows
      setData(prevData => prevData.filter(row => !row.has_phone_conflict));

      toast({
        title: "Conflicting records removed",
        description: "All records with phone conflicts have been marked for removal",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error removing conflicting records:', error);
      toast({
        title: "Error removing records",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsRemoving(false);
    }
  };

  const hasConflicts = useMemo(() => {
    return data.some(row => row.has_phone_conflict);
  }, [data]);

  return (
    <Box p={4}>
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <Heading size="lg">Transfer Member Import</Heading>
        <HStack spacing={4}>
          <Select
            value={transferMode}
            onChange={handleTransferModeChange}
            width="auto"
            size="sm"
          >
            <option value="test">Test Mode</option>
            <option value="production">Production Mode</option>
            <option value="simulation">Simulation Mode</option>
          </Select>
          <ButtonGroup>
            <Button
              colorScheme="blue"
              onClick={() => setShowTransferModal(true)}
              isDisabled={selectedRows.length === 0}
            >
              Transfer Selected ({selectedRows.length})
            </Button>
            <Button
              colorScheme="orange"
              onClick={handleRemoveConflicts}
              isLoading={isRemoving}
              loadingText="Removing..."
              isDisabled={!hasConflicts}
              title={!hasConflicts ? "No phone conflicts found" : "Remove all records with phone conflicts"}
            >
              Remove All Conflicts
            </Button>
            <Button
              colorScheme="red"
              onClick={handleRemoveSelected}
              isLoading={isRemoving}
              loadingText="Removing..."
              isDisabled={selectedRows.length === 0}
            >
              Remove Selected ({selectedRows.length})
            </Button>
          </ButtonGroup>
        </HStack>
      </Flex>

      {/* Transfer Modal */}
      <TransferModal
        isOpen={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        selectedRecords={selectedRows}
        validationSummary={validationSummary}
        transferMode={transferMode}
        testEmail={testEmail}
        onUnselectIncomplete={handleUnselectIncomplete}
        onTransfer={handleTransfer}
      />

      <TransferSummaryDialog 
        isOpen={showSummary} 
        onClose={() => setShowSummary(false)}
        summary={transferSummary}
      />

      <Box 
        border="2px" 
        borderColor="gray.300" 
        borderRadius="md"
        bg="white"
        h="calc(100vh - 200px)"
        display="flex"
        flexDirection="column"
        boxShadow="sm"
      >
        {isRemoving ? (
          <Flex justify="center" align="center" flex="1">
            <Text>Loading data...</Text>
          </Flex>
        ) : data.length === 0 ? (
          <Flex justify="center" align="center" flex="1">
            <Text>No import records found</Text>
          </Flex>
        ) : (
          <Box 
            overflowX="auto"
            overflowY="auto"
            flex="1"
          >
            <Table size="sm" variant="unstyled" style={{ tableLayout: 'fixed' }}>
              <Thead position="sticky" top={0} zIndex={1}>
                {table.getHeaderGroups().map(headerGroup => (
                  <React.Fragment key={headerGroup.id}>
                    <Tr>
                      {headerGroup.headers.map(header => (
                        <Th
                          key={header.id}
                          px={2}
                          py={2}
                          borderBottom="2px"
                          borderRight="1px"
                          borderColor="gray.200"
                          bg="gray.100"
                          _hover={header.column.getCanSort() ? { bg: "gray.200" } : undefined}
                          width={header.column.columnDef.size}
                          minW={header.column.columnDef.size}
                          maxW={header.column.columnDef.size}
                          whiteSpace="normal"
                          textTransform="none"
                        >
                          <ColumnHeader header={header}>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </ColumnHeader>
                        </Th>
                      ))}
                    </Tr>
                  </React.Fragment>
                ))}
              </Thead>
              <Tbody>
                {table.getRowModel().rows.map(row => (
                  <Tr 
                    key={row.id}
                    _hover={{ bg: "gray.50" }}
                    borderBottom="1px"
                    borderColor="gray.100"
                  >
                    {row.getVisibleCells().map(cell => (
                      <Td
                        key={cell.id}
                        px={1}
                        py={0.5}
                        borderRight="1px"
                        borderColor="gray.100"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TransferMemberImport;
